import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NoChargers, ResizingArrow } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { RoutePath } from '../../routes';
import { useFetchChargersQuery } from '../../services/charger.api';
import { Charger, Company } from '../../stores/types';
import { ColorType, Icon, Label, LabelType } from '../_ui';
import { GridPager } from '../_ui/grid/Grid-Pager.component';
import './charger-table/ChargerDataTable.css';
import { ChargerSortColumn } from './charger-table/types';

// Extracted modules
import {
  captureSortColumnEvent,
  DataTableName,
  DataTablePage,
  exportDataTableToCSV,
} from '../_ui/data-table/utils';
import {
  buildFetchOptions,
  filterChargersByStatus,
  getRowBgColor,
  headerMapForCSV,
  mapRowsForCSV,
  paginateChargers,
  ROW_PER_PAGE,
  sortColumns,
} from './charger-table/chargerTableUtils';
import { ChargerDataTableProps, SortConfig } from './charger-table/types';
import { useChargerTableColumns } from './charger-table/useChargerTableColumns';

const CHARGER_TABLE_SORT_CONFIG_STORAGE_KEY = 'chargerTable.sortConfig';

export const ChargerDataTable = memo(
  ({
    status,
    maintenanceFlag,
    locationIds,
    searches,
    page,
    handleLoadPage,
    selectedChargers,
    setSelectedChargers,
    onResetColumnSizingReady,
    columnVisibility = {},
    onExportReady,
  }: ChargerDataTableProps) => {
    const { t } = useTranslation();
    const [isLocalTimezone, setIsLocalTimezone] = useState(true);
    const [sortConfig, setSortConfig] = useLocalStorageState<SortConfig>(
      CHARGER_TABLE_SORT_CONFIG_STORAGE_KEY,
      { column: null, direction: 'asc' },
    );

    const { navigateWithCompanyId } = useNavigateWithSearchParam();
    const { isMultiViewActive, companies, companyId, userScope } = useCompany();
    const { canEditChargers } = useCompanyPermissions(companyId ?? undefined);

    const companyMap = useMemo(() => {
      const map: Record<string, Company> = {};
      companies?.forEach((c) => {
        map[c.id] = c;
      });
      return map;
    }, [companies]);

    // Build fetch options
    const fetchOption = useMemo(
      () => buildFetchOptions(status, maintenanceFlag, locationIds, searches, userScope),
      [status, maintenanceFlag, locationIds, userScope, searches],
    );

    // Fetch chargers
    const { chargerData, isChargerLoading } = useFetchChargersQuery(fetchOption, {
      pollingInterval: 15000,
      selectFromResult: (endPoint) => ({
        chargerData: endPoint.data?.entities || [],
        isChargerLoading: endPoint.isFetching && !endPoint?.data,
      }),
    });

    // Filter, sort, and paginate
    const filteredChargers = useMemo(
      () => filterChargersByStatus(chargerData, status),
      [chargerData, status],
    );

    const sortedChargers = useMemo(
      () => sortColumns(filteredChargers, sortConfig, companyMap),
      [filteredChargers, sortConfig],
    );

    const data = useMemo(() => paginateChargers(sortedChargers, page), [sortedChargers, page]);

    // Handlers
    const rowClick = (charger: Charger) => {
      navigateWithCompanyId(`../${RoutePath.CHARGERS}/${charger.id}`, companyId);
    };

    const handleSort = useCallback((column: ChargerSortColumn) => {
      captureSortColumnEvent(DataTablePage.CHARGERS, DataTableName.CHARGERS, column);
      setSortConfig((prev) => {
        if (prev.column === column) {
          if (prev.direction === 'asc') {
            return { column, direction: 'desc' };
          } else {
            return { column: null, direction: 'asc' };
          }
        } else {
          return { column, direction: 'asc' };
        }
      });
    }, []);

    const handleCheckboxSelection = useCallback(
      (charger: Charger, checked: boolean) => {
        if (checked) {
          setSelectedChargers([...selectedChargers, charger]);
        } else {
          setSelectedChargers(selectedChargers.filter((c) => c.id !== charger.id));
        }
      },
      [selectedChargers, setSelectedChargers],
    );

    const handleSelectAll = useCallback(
      (checked: boolean) => {
        if (checked) {
          const newSelection = [...selectedChargers];
          data.forEach((charger) => {
            if (!newSelection.find((c) => c.id === charger.id)) {
              newSelection.push(charger);
            }
          });
          setSelectedChargers(newSelection);
        } else {
          setSelectedChargers(selectedChargers.filter((c) => !data.find((d) => d.id === c.id)));
        }
      },
      [data, selectedChargers, setSelectedChargers],
    );

    const isAllSelected = useMemo(() => {
      if (data.length === 0) return false;
      return data.every((charger) => selectedChargers.some((c) => c.id === charger.id));
    }, [data, selectedChargers]);

    // Columns from custom hook
    const columns = useChargerTableColumns({
      t,
      isAllSelected,
      handleSelectAll,
      selectedChargers,
      handleCheckboxSelection,
      isChargerLoading,
      isLocalTimezone,
      setIsLocalTimezone,
      isMultiViewActive,
      companyMap,
      sortConfig,
      handleSort,
      canEditChargers,
    });

    const table = useReactTable({
      data,
      columns,
      state: {
        columnVisibility,
      },
      autoResetPageIndex: false,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      columnResizeMode: 'onChange',
      enableColumnResizing: true,
    });

    // Export functionality
    const exportToCSV = useCallback(() => {
      const visibleColumnIds = table
        .getVisibleLeafColumns()
        .filter((col) => col.id !== 'select')
        .map((col) => col.id);
      exportDataTableToCSV(
        mapRowsForCSV(sortedChargers, visibleColumnIds, companies, t),
        headerMapForCSV(t),
        visibleColumnIds,
        'chargers_export',
        DataTablePage.CHARGERS,
        DataTableName.CHARGERS,
      );
      // eslint-disable-next-line
    }, [sortedChargers, t, companies]);

    useEffect(() => {
      if (onExportReady) {
        onExportReady(exportToCSV);
      }
    }, [exportToCSV, onExportReady]);

    useEffect(() => {
      if (onResetColumnSizingReady) {
        onResetColumnSizingReady(() => table.resetColumnSizing());
      }
    }, [onResetColumnSizingReady, table]);

    // Empty state
    if (!isChargerLoading && sortedChargers.length === 0) {
      return (
        <div className='flex flex-col gap-1 items-center justify-center py-4 h-52 mt-4 mb-7'>
          <Icon src={NoChargers} size={IconSize.SIZE_48x48} />
          <Label text={t('charger_not_found')} type={LabelType.H4} color={ColorType.GREY5} />
          <Label
            text={t('charger_not_found_message')}
            type={LabelType.BODY2}
            color={ColorType.GREY5}
          />
        </div>
      );
    }

    const totalPages = Math.ceil(sortedChargers.length / ROW_PER_PAGE);

    return (
      <div className='overflow-visible mt-5'>
        <div className='overflow-x-auto overflow-y-visible charger-table-scroll'>
          <table className='charger-data-table' style={{ width: table.getTotalSize() }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className='bg-white'>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      data-column-id={header.column.id}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize ?? 100,
                        position: 'relative',
                      }}
                      className='px-4 py-3 text-left text-sm font-semibold font-inter text-grey6'
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}

                      {/* Resize Handle */}
                      {header.column.getCanResize() && (
                        <div
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className='resizer'
                          style={{
                            cursor: `url(${ResizingArrow}) 8 8, col-resize`,
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`hover:bg-grey1 cursor-pointer ${getRowBgColor(row.original)}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                    rowClick(row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                        maxWidth: cell.column.getSize(),
                      }}
                      className='px-4 py-3 text-sm text-gray-900 overflow-hidden whitespace-nowrap'
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <GridPager
            totalPage={totalPages}
            currentPage={page}
            loadPage={handleLoadPage}
            showPageSearch
          />
        )}
      </div>
    );
  },
);

ChargerDataTable.displayName = 'ChargerDataTable';
